import React, { useEffect, useState, lazy, Suspense } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { hot } from 'react-hot-loader/root';
import { Provider, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { ReactReduxFirebaseProvider, isLoaded } from 'react-redux-firebase';
import { ConfigProvider, Spin } from 'antd';
import store, { rrfProps } from './redux/store';
import Admin from './routes/admin';
import Auth from './routes/auth';
import './static/css/style.css';
import './static/css/scheduling-theme.css';
import config from './config/config';
import ProtectedRoute from './components/utilities/protectedRoute';
// Pre-built, minified CSS instead of the raw .less source — importing
// antd.less means shipping (and, worse, LESS-compiling) the ENTIRE
// component library's uncompressed styles on every single load, for
// every visitor, regardless of which antd components the app actually
// uses. There's no custom theme/modifyVars anywhere in this codebase
// (checked — nothing overrides an @antd-variable), so nothing here
// actually needs the .less source; antd.min.css is the same visual
// result, already compiled, already minified, a fraction of the size,
// and needs zero LESS compilation at request time. This alone is
// almost certainly the biggest single contributor to slow CSS loads.
import 'antd/dist/antd.less';

/* ── Super-admin pages (no Firebase/Redux dependency) ──
 * Lazy-loaded: SuperAdminApp below returns null immediately for every
 * visitor NOT on a /superadmin* path (i.e. everyone using the actual
 * scheduling app), but these were previously plain top-level imports —
 * meaning every regular user's initial bundle included the full
 * super-admin dashboard's code whether they'd ever see it or not.
 * lazy() means that code is only fetched if/when someone actually
 * navigates to /superadmin*. */
const SuperAdminLogin         = lazy(() => import('./container/superadmin/SuperAdminLogin'));
const SuperAdminDashboard     = lazy(() => import('./container/superadmin/SuperAdminDashboard'));
const SuperAdminEvents        = lazy(() => import('./container/superadmin/SuperAdminEvents'));
const SuperAdminUsers         = lazy(() => import('./container/superadmin/SuperAdminUsers'));
const SuperAdminTennisMatches = lazy(() => import('./container/superadmin/SuperAdminTennisMatches'));
const SuperAdminOnetoone      = lazy(() => import('./container/superadmin/SuperAdminOnetoone'));

const { theme } = config;

/* NOTE: There used to be a standalone TennisApp here, rendering
 * /tennis-admin and /match-details as a fully separate, layout-less
 * <Router> instance. That's gone now — those pages are registered as
 * ordinary routes in routes/auth.js's FrontendRoutes, right next to
 * the event module's /view-events and /:id/:title, so they:
 *   (a) render inside the same withAdminLayout chrome every other
 *       page uses, instead of a bespoke standalone header, and
 *   (b) share the SAME <Router>/history as the rest of the app, so
 *       history.push('/match-details') from CreateMatchModal actually
 *       navigates there instead of pushing a URL a sibling, untethered
 *       Router never saw. */

/** Standalone router for /superadmin-* and /superadmin/* routes.
 *  These paths match before the Firebase-backed Router renders,
 *  so they never touch Redux/Firebase state. */
function SuperAdminApp() {
  const path = window.location.pathname;
  const isSuperAdmin =
    path.startsWith('/superadmin-login') || path.startsWith('/superadmin');
  if (!isSuperAdmin) return null;

  return (
    <Router basename={process.env.PUBLIC_URL}>
      <Suspense fallback={<div className="spin"><Spin /></div>}>
        <Switch>
          <Route exact path="/superadmin-login"           component={SuperAdminLogin} />
          <Route exact path="/superadmin/dashboard"       component={SuperAdminDashboard} />
          <Route exact path="/superadmin/events"          component={SuperAdminEvents} />
          <Route exact path="/superadmin/tennis-matches"  component={SuperAdminTennisMatches} />
          <Route exact path="/superadmin/onetoone"        component={SuperAdminOnetoone} />
          <Route exact path="/superadmin/users"           component={SuperAdminUsers} />
          {/* Default redirect from /superadmin → /superadmin/dashboard */}
          <Route exact path="/superadmin">
            {() => { window.location.replace('/superadmin/dashboard'); return null; }}
          </Route>
        </Switch>
      </Suspense>
    </Router>
  );
}

const ProviderConfig = () => {
  const { rtl, isLoggedIn, topMenu, darkMode, auth } = useSelector(state => ({
    darkMode: state.ChangeLayoutMode.data,
    rtl: state.ChangeLayoutMode.rtlData,
    topMenu: state.ChangeLayoutMode.topMenu,
    isLoggedIn: state.auth.login,
    auth: state.fb.auth,
  }));

  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    let unmounted = false;
    if (!unmounted) setPath(window.location.pathname);
    return () => { unmounted = true; };
  }, [setPath]);

  /* Don't render the main Firebase app at all while on super-admin routes.
   * Tennis routes (/tennis-admin, /match-details) now go through this
   * Firebase-backed router like everything else — see routes/auth.js. */
  const isSuperAdmin =
    path.startsWith('/superadmin-login') || path.startsWith('/superadmin');
  if (isSuperAdmin) return null;

  return (
    <ConfigProvider direction={rtl ? 'rtl' : 'ltr'}>
      <ThemeProvider theme={{ ...theme, rtl, topMenu, darkMode }}>
        <ReactReduxFirebaseProvider {...rrfProps}>
          {!isLoaded(auth) ? (
            <div className="spin"><Spin /></div>
          ) : (
            <Router basename={process.env.PUBLIC_URL}>
              {!isLoggedIn
                ? <Route path="/" component={Auth} />
                : <ProtectedRoute path="/admin" component={Admin} />}
              {isLoggedIn &&
                (path === process.env.PUBLIC_URL ||
                  path === `${process.env.PUBLIC_URL}/`) && (
                  <Redirect to="/admin" />
                )}
            </Router>
          )}
        </ReactReduxFirebaseProvider>
      </ThemeProvider>
    </ConfigProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <SuperAdminApp />
      <ProviderConfig />
    </Provider>
  );
}

export default hot(App);